/**
 * NEXUS UI - DonutChart
 * Circular proportion chart with center content
 */

import React, { useState, useMemo } from 'react';
import type { BaseProps, NexusVariant } from './types';

interface DonutSegment {
  label: string;
  value: number;
  color?: NexusVariant;
}

export interface DonutChartProps extends BaseProps {
  data: DonutSegment[];
  size?: number;
  thickness?: number;
  showLegend?: boolean;
  showLabels?: boolean;
  showTooltip?: boolean;
  animated?: boolean;
  centerContent?: React.ReactNode;
  centerLabel?: string;
  centerValue?: string | number;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const defaultColors: NexusVariant[] = ['cyan', 'blue', 'violet', 'green', 'amber', 'red'];

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  size = 200,
  thickness = 24,
  showLegend = true,
  showLabels = false,
  showTooltip = true,
  animated = true,
  centerContent,
  centerLabel,
  centerValue,
  className = '',
  style,
}) => {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);

  const center = size / 2;
  const radius = (size - thickness) / 2 - 4;
  const circumference = 2 * Math.PI * radius;

  const { segments } = useMemo(() => {
    const totalValue = data.reduce((sum, d) => sum + d.value, 0);
    let currentOffset = 0;

    const processedSegments = data.map((d, i) => {
      const percentage = (d.value / totalValue) * 100;
      const length = (percentage / 100) * circumference;
      const offset = currentOffset;
      currentOffset += length;

      const color = d.color || defaultColors[i % defaultColors.length];
      const midAngle = ((offset + length / 2) / circumference) * 360 - 90;
      const midAngleRad = (midAngle * Math.PI) / 180;

      return {
        ...d,
        percentage,
        length,
        offset,
        color,
        midAngle,
        labelX: center + (radius + thickness / 2 + 20) * Math.cos(midAngleRad),
        labelY: center + (radius + thickness / 2 + 20) * Math.sin(midAngleRad),
      };
    });

    return { segments: processedSegments, total: totalValue };
  }, [data, circumference, center, radius, thickness]);

  const glowId = `donut-glow-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <div
      className={`nx-donut-chart ${className}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '24px',
        ...style,
      }}
    >
      {/* Chart */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: 'rotate(-90deg)' }}
          onMouseLeave={() => setHoveredSegment(null)}
        >
          <defs>
            <filter id={glowId}>
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Background track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--nx-void-elevated)"
            strokeWidth={thickness}
          />

          {/* Segments */}
          {segments.map((segment, index) => {
            const colors = variantColors[segment.color];
            const isHovered = hoveredSegment === index;

            return (
              <g key={index}>
                {/* Glow layer */}
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={colors.base}
                  strokeWidth={thickness + 4}
                  strokeDasharray={`${segment.length} ${circumference - segment.length}`}
                  strokeDashoffset={-segment.offset}
                  strokeLinecap="round"
                  opacity={isHovered ? 0.5 : 0.2}
                  filter={`url(#${glowId})`}
                  style={{
                    transition: animated ? 'all 0.3s ease-out' : 'none',
                  }}
                />
                {/* Main segment */}
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={colors.base}
                  strokeWidth={isHovered ? thickness + 4 : thickness}
                  strokeDasharray={`${segment.length} ${circumference - segment.length}`}
                  strokeDashoffset={-segment.offset}
                  strokeLinecap="round"
                  onMouseEnter={() => setHoveredSegment(index)}
                  style={{
                    cursor: 'pointer',
                    transition: animated ? 'all 0.3s ease-out' : 'none',
                    filter: isHovered ? `url(#${glowId})` : 'none',
                  }}
                />
              </g>
            );
          })}
        </svg>

        {/* Center content */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {centerContent || (
            <>
              {centerValue !== undefined && (
                <div
                  style={{
                    fontFamily: 'var(--nx-font-display)',
                    fontSize: size > 150 ? '28px' : '20px',
                    fontWeight: 700,
                    color: 'var(--nx-text-primary)',
                    lineHeight: 1,
                  }}
                >
                  {centerValue}
                </div>
              )}
              {centerLabel && (
                <div
                  style={{
                    fontFamily: 'var(--nx-font-interface)',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--nx-text-tertiary)',
                    marginTop: '4px',
                  }}
                >
                  {centerLabel}
                </div>
              )}
            </>
          )}
        </div>

        {/* Segment labels */}
        {showLabels && (
          <svg
            width={size}
            height={size}
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          >
            {segments.map((segment, index) => (
              <text
                key={index}
                x={segment.labelX}
                y={segment.labelY}
                textAnchor={segment.labelX > center ? 'start' : 'end'}
                dominantBaseline="middle"
                fill={variantColors[segment.color].base}
                fontSize="10"
                fontFamily="var(--nx-font-mono)"
              >
                {segment.percentage.toFixed(0)}%
              </text>
            ))}
          </svg>
        )}

        {/* Tooltip */}
        {showTooltip && hoveredSegment !== null && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -150%)',
              background: 'var(--nx-glass-dark)',
              border: `1px solid ${variantColors[segments[hoveredSegment].color].base}`,
              borderRadius: '4px',
              padding: '8px 12px',
              boxShadow: `0 0 16px ${variantColors[segments[hoveredSegment].color].glow}`,
              pointerEvents: 'none',
              zIndex: 10,
              whiteSpace: 'nowrap',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--nx-font-interface)',
                fontSize: '12px',
                color: 'var(--nx-text-primary)',
                marginBottom: '2px',
              }}
            >
              {segments[hoveredSegment].label}
            </div>
            <div
              style={{
                fontFamily: 'var(--nx-font-mono)',
                fontSize: '14px',
                fontWeight: 600,
                color: variantColors[segments[hoveredSegment].color].base,
              }}
            >
              {segments[hoveredSegment].value} ({segments[hoveredSegment].percentage.toFixed(1)}%)
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            paddingTop: '8px',
          }}
        >
          {segments.map((segment, index) => {
            const colors = variantColors[segment.color];
            const isHovered = hoveredSegment === index;

            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: isHovered ? 'var(--nx-glass-frosted)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-out',
                }}
                onMouseEnter={() => setHoveredSegment(index)}
                onMouseLeave={() => setHoveredSegment(null)}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    background: colors.base,
                    boxShadow: isHovered ? `0 0 8px ${colors.glow}` : 'none',
                  }}
                />
                <span
                  style={{
                    fontFamily: 'var(--nx-font-interface)',
                    fontSize: '12px',
                    color: isHovered ? 'var(--nx-text-primary)' : 'var(--nx-text-secondary)',
                  }}
                >
                  {segment.label}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--nx-font-mono)',
                    fontSize: '11px',
                    color: isHovered ? colors.base : 'var(--nx-text-tertiary)',
                    marginLeft: 'auto',
                  }}
                >
                  {segment.percentage.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
